import { LITERALLY_ANYTHING_ID, usePacketInput } from '../Contexts/PacketInputContext';
import { InformationCircleIcon } from '@heroicons/react/16/solid';
import { Dropdown } from './Dropdown';
import typesOfReadings from '../../../../public/json/types-of-readings.json';
import { WithTooltip } from '../../../components/WithTooltip';
import { usePacketBase } from '../Contexts/PacketBaseContext';
import Image from '../../../components/Image';
import {
  getBlockNamesFromTemplate,
  useTemplateContext,
} from '../../Packet/Contexts/TemplateContext';
import { Mixpanel } from '../../../services/Mixpanel';

export const AdvancedOptions = ({ isOpen }) => {
  return (
    <div className={isOpen ? 'flex flex-col sm:flex-row gap-2 sm:gap-0 mt-3' : 'hidden'}>
      <Genre />
      <Format />
      <Template />
    </div>
  );
};

const Genre = () => {
  const { inputType, genre, setGenre } = usePacketInput();
  return (
    inputType.id === LITERALLY_ANYTHING_ID && (
      <div>
        <div className='flex gap-x-1 items-center text-gray-700 text-xs pl-2 pe-2 me-3'>
          Generated Reading Type
          <WithTooltip
            text="The type of reading we'll generate with your resources"
            position='top'
            anchor='center'
            showArrow={true}>
            <InformationCircleIcon className='w-4 h-3 text-gray-400' />
          </WithTooltip>
        </div>

        <div className='my-2 w-fit min-w-44 bg-gray-50 rounded-md'>
          <Dropdown
            name='Reading Type'
            optionList={typesOfReadings}
            selected={genre}
            setSelected={setGenre}
            extraClasses='outline outline-1 outline-offset-[-1px] outline-gray-100'
          />
        </div>
      </div>
    )
  );
};

const Format = () => {
  const { orientation, setOrientation, setTemplateId } = usePacketBase();
  const { inputType } = usePacketInput();
  return (
    <div className='h-full flex flex-col'>
      <div className='flex gap-x-1 items-center text-gray-700 text-xs pl-2'>
        Format
        <WithTooltip
          text="The format of the resources we'll generate"
          position='top'
          anchor={inputType.id === LITERALLY_ANYTHING_ID ? 'center' : 'left'}
          showArrow={true}>
          <InformationCircleIcon className='w-4 h-3 text-gray-400' />
        </WithTooltip>
      </div>
      <div className='my-2 w-fit bg-gray-50 rounded-lg text-sm outline outline-1 outline-offset-[-1px] outline-gray-100 p-0.5 flex gap-x-1 group'>
        <button
          className={`pl-2 pr-2 py-[7px] sm:py-[5px] rounded-md ${orientation === '8.5x11' ? 'bg-white border-gray-300 border text-gray-900' : 'border-transparent border hover:bg-gray-100 text-gray-700 hover:text-gray-900'}`}
          onClick={() => {
            Mixpanel.track('Format Selected', { from: orientation, to: '8.5x11' });
            setOrientation('8.5x11');
            setTemplateId(null);
          }}>
          <span className='flex items-center gap-x-1'>
            <Image
              src={`/homepage-doc-icon.svg`}
              className={`w-4 h-4 ${orientation === '8.5x11' ? 'opacity-90' : 'opacity-70 group-hover:opacity-90'}`}
              alt='Pages'
            />
            Pages
          </span>
        </button>
        <button
          className={`pl-2 pr-2 py-[7px] sm:py-[5px] rounded-md ${orientation === '16x9' ? 'bg-white border-gray-300 border text-gray-900' : 'border-transparent border hover:bg-gray-100 text-gray-700 hover:text-gray-900'}`}
          onClick={() => {
            Mixpanel.track('Format Selected', { from: orientation, to: '16x9' });
            setOrientation('16x9');
            setTemplateId(null);
          }}>
          <span className='flex items-center gap-x-1'>
            <Image
              src={`/homepage-slide-icon.svg`}
              className={`w-4 h-4 ${orientation === '16x9' ? 'opacity-90' : 'opacity-70 group-hover:opacity-90'}`}
              alt='Slides'
            />
            Slides
          </span>
        </button>
      </div>
    </div>
  );
};

const DEFAULT_TEMPLATE = {
  name: 'Default',
  description: 'Auto-selected set of activities',
  value: null,
};

const Template = () => {
  const { templateId, setTemplateId, orientation } = usePacketBase();
  const { getTemplatesByOrientation } = useTemplateContext();
  const { inputType } = usePacketInput();

  const userTemplates = getTemplatesByOrientation(orientation);

  if (!userTemplates || userTemplates.length === 0) {
    return null;
  }

  const options = userTemplates.map((template) => ({
    label: template.title,
    value: template.id,
    name: template.title,
    description: getBlockNamesFromTemplate(template),
  }));

  // if there's a selected template, add the default template to the options
  if (templateId) {
    options.unshift(DEFAULT_TEMPLATE);
  }

  let selectedTemplate = userTemplates.find((template) => template.id === templateId);
  if (selectedTemplate) {
    selectedTemplate = {
      name: selectedTemplate.title,
      value: selectedTemplate.id,
    };
  } else {
    selectedTemplate = {
      name: 'Select a Template',
      value: null,
    };
  }

  return (
    <div className='h-full flex flex-col sm:ms-4'>
      <div className='flex gap-x-1 items-center text-gray-700 text-xs pl-2'>
        Template
        <WithTooltip
          text='Use a template for your resources'
          position='top'
          anchor={inputType.id === LITERALLY_ANYTHING_ID ? 'center' : 'left'}
          showArrow={true}>
          <InformationCircleIcon className='w-4 h-3 text-gray-400' />
        </WithTooltip>
      </div>
      <div className='my-2 w-fit min-w-44 bg-gray-50 rounded-md'>
        <Dropdown
          name='Template'
          optionList={options}
          selected={selectedTemplate}
          setSelected={(template) => {
            setTemplateId(template.value);
          }}
          extraClasses='outline outline-1 outline-offset-[-1px] outline-gray-100'
        />
      </div>
    </div>
  );
};
